import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API_URL } from '../config/app';

type VerifyStatus = 'idle' | 'loading' | 'success' | 'error';

const REDIRECT_DELAY = 4;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerifyStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [countdown, setCountdown] = useState(REDIRECT_DELAY);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMessage('Lien invalide. Aucun token de vérification trouvé.');
      return;
    }

    const verifyToken = async () => {
      setStatus('loading');
      try {
        const response = await fetch(
          `${API_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`
        );

        if (response.ok) {
          setStatus('success');
        } else {
          const data = await response.json().catch(() => ({}));
          setErrorMessage(
            data?.error || 'Une erreur est survenue lors de la vérification.'
          );
          setStatus('error');
        }
      } catch {
        setErrorMessage('Impossible de joindre le serveur. Veuillez réessayer.');
        setStatus('error');
      }
    };

    verifyToken();
  }, [searchParams]);

  useEffect(() => {
    if (status !== 'success') return;
    if (countdown <= 0) {
      navigate('/mon-espace');
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, navigate]);

  const cardVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
  };

  const iconVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { type: 'spring' as const, stiffness: 260, damping: 20, delay: 0.2 } },
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#2F3136]">
      <motion.div
        className="w-full max-w-md bg-[#36393F] rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-6"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {status === 'loading' && (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <Loader2 className="w-14 h-14 text-[#5865F2]" />
            </motion.div>
            <p className="text-gray-300 text-base font-medium">
              Vérification en cours...
            </p>
          </>
        )}

        {status === 'idle' && (
          <p className="text-gray-400 text-sm">Initialisation...</p>
        )}

        {status === 'success' && (
          <>
            <motion.div variants={iconVariants} initial="hidden" animate="visible">
              <CheckCircle className="w-16 h-16 text-green-400" strokeWidth={1.5} />
            </motion.div>
            <div className="text-center space-y-2">
              <h1 className="text-white text-xl font-bold">Email vérifié !</h1>
              <p className="text-gray-300 text-sm">
                Votre compte a été activé avec succès.
              </p>
              <p className="text-gray-400 text-sm">
                Redirection vers votre espace dans <span className="text-white font-semibold">{countdown}</span> seconde{countdown > 1 ? 's' : ''}...
              </p>
            </div>
            <button
              onClick={() => navigate('/mon-espace')}
              className="mt-2 w-full bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold py-2.5 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#5865F2] focus:ring-offset-2 focus:ring-offset-[#36393F]"
            >
              Accéder maintenant
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <motion.div variants={iconVariants} initial="hidden" animate="visible">
              <XCircle className="w-16 h-16 text-red-400" strokeWidth={1.5} />
            </motion.div>
            <div className="text-center space-y-2">
              <h1 className="text-white text-xl font-bold">Échec de la vérification</h1>
              <p className="text-gray-300 text-sm">{errorMessage}</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="mt-2 w-full border border-gray-500 hover:border-gray-300 text-gray-300 hover:text-white font-semibold py-2.5 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-[#36393F]"
            >
              Retour à l'accueil
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
